import { BaseCommandResult, BaseDto, ItemResult } from '../types';
import { AbstractValidator } from 'fluent-ts-validator';
import i18n from '../../i18n';
import { repository } from 'redux-scaffolding-ts';
import { DataStore } from '../dataStore';
import { FormStore } from '../formStore';
import HttpService from 'services/http-service';
import { container } from 'inversify.config';

export interface eventCheckListRoleUserCheck {
  description: string;
  checked: boolean;
  comment: string;
  isMandatory: boolean;
  checkListId?: string;
  isCheckListNew?: boolean;
  templateId?: string;
}

export interface eventCheckListRoleUserDto extends BaseDto {
  id: string;
  eventId: string;
  role: string;
  userId: string;
  locationId: string;
  roleUserChecks: eventCheckListRoleUserCheck[];
}

export interface CreateEventCheckListRoleUserDto {
  eventId: string;
  role: string;
  userId: string;
  locationId: string;
  roleUserChecks: eventCheckListRoleUserCheck[];
}

export interface ChangeEventCheckListRoleUserDto {
  id: string;
  eventId: string;
  role: string;
  userId: string;
  locationId: string;
  roleUserChecks: eventCheckListRoleUserCheck[];
}

export interface EventChecklistUploadedFileDto {
  id?: string;
  eventId?: string;
  role?: string;
  userId?: string;
  locationId?: string | null;
  originalName: string;
  uploadedName: string;
}

export interface ChecklistUserFileUploadDto {
  uploadedFileName: string;
  temporaryId: string;
}

export interface ChecklistUserFileUploadContext {
  eventId: string;
  role: string;
  userName: string;
  locationId?: string | null;
  eventTypeName?: string;
  friendlyEventId?: string;
}

export interface CreateChecklistEventFileDto {
  eventId: string;
  role: string;
  userId?: string;
  locationId?: string | null;
  originalName: string;
  uploadedName: string;
}

export interface DeleteChecklistEventFileDto {
  id?: string;
  uploadedName?: string;
}

export class ChecklistRoleUserFileService {
  //public static baseUrl = 'http://localhost:7071/api/v1';
  public static baseUrl = 'events/v1';
  public static uploadPath = 'upload-checklist-userfile';
  public static createEventFilePath = 'create-checklist-event-file';
  public static getEventFilesPath = 'get-checklist-event-files';
  public static downloadEventFilePath = 'download-checklist-event-file';

  public static async upload(file: File, context: ChecklistUserFileUploadContext) {
    const formData = new FormData();
    formData.append('file', file);

    const query = new URLSearchParams({
      eventId: context.eventId,
      role: context.role,
      userName: context.userName
    });
    if (context.locationId) query.append('locationId', context.locationId);
    if (context.eventTypeName) query.append('eventTypeName', context.eventTypeName);
    if (context.friendlyEventId) query.append('friendlyEventId', context.friendlyEventId);

    const httpService = container.get(HttpService);
    return httpService.postMultipart<ItemResult<ChecklistUserFileUploadDto>>(
      `${ChecklistRoleUserFileService.baseUrl}/${ChecklistRoleUserFileService.uploadPath}?${query.toString()}`,
      formData
    );
  }

  public static async createEventFile(file: CreateChecklistEventFileDto) {
    const httpService = container.get(HttpService);
    return httpService.post<CreateChecklistEventFileDto, BaseCommandResult>(
      `${ChecklistRoleUserFileService.baseUrl}/${ChecklistRoleUserFileService.createEventFilePath}`,
      file
    );
  }

  public static async deleteEventFile(file: DeleteChecklistEventFileDto) {
    const httpService = container.get(HttpService);
    const url = file.id
      ? `${ChecklistRoleUserFileService.baseUrl}/delete-checklist-event-file/${file.id}`
      : `${ChecklistRoleUserFileService.baseUrl}/delete-checklist-event-file`;
    return file.id
      ? httpService.delete<DeleteChecklistEventFileDto, BaseCommandResult>(url)
      : httpService.delete<DeleteChecklistEventFileDto, BaseCommandResult>(url, file);
  }

  public static async getEventFiles(eventId: string, role: string, userId?: string, locationId?: string | null) {
    const httpService = container.get(HttpService);
    return httpService.get<{
      isSuccess: boolean;
      items?: EventChecklistUploadedFileDto[];
      item?: EventChecklistUploadedFileDto[];
    }>(`${ChecklistRoleUserFileService.baseUrl}/${ChecklistRoleUserFileService.getEventFilesPath}`, {
      eventId,
      role,
      userId,
      locationId
    });
  }

  public static async downloadEventFile(id: string, fileName: string) {
    const httpService = container.get(HttpService);
    const result = await httpService.get<ItemResult<string>>(
      `${ChecklistRoleUserFileService.baseUrl}/${ChecklistRoleUserFileService.downloadEventFilePath}/${id}`
    );
    if (!result.data.isSuccess || !result.data.item) {
      throw new Error('File download failed');
    }

    const response = await fetch(result.data.item);
    if (!response.ok) {
      throw new Error('File download failed');
    }

    const blob = await response.blob();
    const FileSaver = await import('file-saver');
    FileSaver.saveAs(blob, fileName);
  }
}

export async function uploadEventChecklistUserFile(file: File, context: ChecklistUserFileUploadContext) {
  return ChecklistRoleUserFileService.upload(file, context);
}

export interface EventCheckListRoleUserNewItemDto {
  id: string | null;
  checkListId: string;
  checked: boolean;
  comment: string | null;
}

export interface UpdateEventCheckListRoleUserNewItemDto {
  id?: string;
  eventId: string;
  checkListId: string;
  role: string;
  userId?: string;
  locationId?: string | null;
  checked: boolean;
  comment?: string | null;
}

export interface EventCheckListRoleUserNewGroupDto {
  eventId: string;
  role: string;
  userId: string;
  locationId: string | null;
  items: EventCheckListRoleUserNewItemDto[];
}

export interface UpdateEventCheckListRoleUserNewBatchDto {
  eventId: string;
  groups: EventCheckListRoleUserNewGroupDto[];
}

export class CreateEventCheckListRoleUserValidator extends AbstractValidator<CreateEventCheckListRoleUserDto> {
  constructor() {
    super();

    this.validateIfString(o => o.role)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Role is required')));

    this.validateIfString(o => o.eventId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Event Id is required')));

    this.validateIfIterable(o => o.roleUserChecks)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('checkList is required')));

    this.validateIfIterable(o => o.roleUserChecks)
      .fulfills(checks => Array.from(checks).every(c => !c.isMandatory || (c.comment && c.comment.trim().length > 0)))
      .withFailureMessage(String(i18n.t('Mandatory comments must be filled before saving')));
  }
}

export class ChangeEventCheckListRoleUserValidator extends AbstractValidator<ChangeEventCheckListRoleUserDto> {
  constructor() {
    super();

    this.validateIfString(o => o.id)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Id is required')));

    this.validateIfString(o => o.eventId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Event Type Id is required')));

    this.validateIfIterable(o => o.roleUserChecks)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('checkList is required')));

    this.validateIfIterable(o => o.roleUserChecks)
      .fulfills(checks => Array.from(checks).every(c => !c.isMandatory || (c.comment && c.comment.trim().length > 0)))
      .withFailureMessage(String(i18n.t('Mandatory comments must be filled before saving')));
  }
}

export class EventCheckListRoleUserNewValidator extends AbstractValidator<EventCheckListRoleUserNewGroupDto> {
  constructor() {
    super();

    this.validateIfString(o => o.eventId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Event Id is required')));

    this.validateIfString(o => o.role)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Role is required')));

    this.validateIfString(o => o.userId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Instructor Id is required')));

    this.validateIfIterable(o => o.items)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('checkList is required')));
  }
}

@repository('@@EVENTCHECKLISTSROLEUSER', 'eventchecklistsroleuser.summary')
export class EventCheckListRoleUserStore extends DataStore<eventCheckListRoleUserDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = 'create-roleUserChecklist';
  retrievePath = 'get-roleUserChecklists';
  updatePath = 'update-roleUserChecklist';
  deletePath = '';

  protected validate(item: eventCheckListRoleUserDto) {
    return new ChangeEventCheckListRoleUserValidator().validate(item);
  }

  constructor() {
    super('EVENTCHECKLISTROLEUSER', {
      isBusy: false,
      items: [],
      count: 0,
      result: undefined,
      discard: item => {}
    });
  }
}

@repository('@@EVENTCHECKLISTSROLEUSER', 'eventchecklistroleuser.new')
export class NewEventCheckListRoleUserStore extends FormStore<CreateEventCheckListRoleUserDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = 'create-roleUserChecklist';
  retrievePath = 'get-roleUserChecklists';
  updatePath = 'update-roleUserChecklist';

  protected validate(item: CreateEventCheckListRoleUserDto) {
    return new CreateEventCheckListRoleUserValidator().validate(item);
  }

  constructor() {
    super('NEW_EVENTCHECKLISTSROLEUSER', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    });
  }
}

@repository('@@EVENTCHECKLISTSROLEUSER', 'eventchecklistroleuser.change')
export class ChangeEventCheckListRoleUserStore extends FormStore<ChangeEventCheckListRoleUserDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = 'create-roleUserChecklist';
  retrievePath = 'get-roleUserChecklists';
  updatePath = 'update-roleUserChecklist';

  protected validate(item: ChangeEventCheckListRoleUserDto) {
    return new ChangeEventCheckListRoleUserValidator().validate(item);
  }

  constructor() {
    super('CHANGE_EVENTCHECKLISTSROLEUSER', {
      isBusy: false,
      status: 'Modified',
      item: undefined,
      result: undefined
    });
  }
}

@repository('@@EVENTCHECKLISTSROLEUSERNEW', 'eventchecklistsroleuser.new.summary')
export class EventCheckListRoleUserStoreNew extends DataStore<EventCheckListRoleUserNewGroupDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = '';
  retrievePath = 'get-roleUserChecklists-new';
  updatePath = 'update-roleUserChecklists-new-batch';
  deletePath = '';

  protected validate(item: EventCheckListRoleUserNewGroupDto) {
    return new EventCheckListRoleUserNewValidator().validate(item);
  }

  constructor() {
    super('EVENTCHECKLISTSROLEUSERNEW', {
      isBusy: false,
      items: [],
      count: 0,
      result: undefined,
      discard: item => {}
    });
  }
}
